import React, { ComponentProps, ComponentType, useEffect } from 'react';
import noop from 'lodash/noop';
import styled from 'styled-components';
import { IconClose } from '../icon/IconClose';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { TransitionZoomIn } from '../transition/TransitionZoomIn';
import { TransitionProps } from '../transition/makeTransitionComponent';
import { Backdrop } from './Backdrop';

export interface DialogTitleProps extends ComponentProps<typeof Flex> {
    onClose: VoidFunction;
}

export const DialogTitle = (props: DialogTitleProps) => {
    const { children, onClose, ...other } = props;

    return (
        <Flex
            px={{ xs: 2, sm: 3 }}
            py={2}
            overflow="hidden"
            width="100%"
            alignItems="center"
            direction="row"
            position="relative"
            background="var(--background-primary)"
            borderBottom="1px solid var(--divider)"
            {...other}
        >
            {children}
            <Box position="absolute" right={1} top="50%" transform="translateY(-50%)">
                <IconButton onClick={onClose}>
                    <IconClose />
                </IconButton>
            </Box>
        </Flex>
    );
};

export const DialogContent = styled(Flex).attrs((props) => ({
    py: { xs: 2, sm: 3 },
    px: { xs: 2, sm: 3 },
    overflowY: 'auto',
    width: '100%',
    height: { xs: '100%', sm: 'auto' },
    background: 'var(--background-primary)',
    ...props,
}))``;

export const DialogActions = styled(Flex).attrs((props) => ({
    px: { xs: 2, sm: 3 },
    pb: { xs: 2, sm: 3 },
    spacing: { xs: 2, sm: 3 },
    width: '100%',
    background: 'var(--background-primary)',
    ...props,
    direction: 'row',
}))``;

interface DialogProps extends ComponentProps<typeof Box> {
    TransitionComponent?: ComponentType<TransitionProps>;
    onClose?: VoidFunction;
}

export const Dialog = (props: DialogProps) => {
    const { children, onClose = noop, TransitionComponent = TransitionZoomIn, ...other } = props;

    useEffect(() => {
        window.document.body.style.setProperty('overflow', 'hidden');

        return () => {
            window.document.body.style.removeProperty('overflow');
        };
    }, []);

    return (
        <Backdrop onClose={onClose}>
            <TransitionComponent>
                <Flex
                    direction="column"
                    overflow="hidden"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto !important' }}
                    maxWidth={{ sm: 580 }}
                    maxHeight={{ sm: '80vh' }}
                    borderRadius={{ sm: 3 }}
                    {...other}
                >
                    {children}
                </Flex>
            </TransitionComponent>
        </Backdrop>
    );
};
